<template>
	<div class="action-wrapper" :data-size="size" :data-color="color" :style="useColorStyle(color ?? null)">
		<button type="button" class="action" :class="[{ disabled: disabled || loading, loading }, label ? 'has-label' : '']" :aria-label="title" :title="title"
		        :data-active="active ? true : undefined" :data-muted="muted ? true : undefined"
		        :disabled="disabled || loading" @click="triggerHaptic">
			<template v-if="loading">
				<loading-button-indicator class="action-loading"></loading-button-indicator>
			</template>
			<template v-else-if="icon">
				<cfl-icon :icon="icon" class="action-icon"></cfl-icon>
			</template>
			<slot></slot>
			<template v-if="label">
				<span class="action-label" :class="labelClass">{{ label }}</span>
			</template>
			<template v-if="iconSuffix && ! loading">
				<cfl-icon :icon="iconSuffix" class="action-icon"></cfl-icon>
			</template>
			<template v-if="shortcut">
				<keyboard-shortcut
					:shift="shortcutShift"
					:control="shortcutControl"
					:label-shortcut="shortcut">
				</keyboard-shortcut>
			</template>
		</button>
	</div>
</template>

<script lang="ts" setup>
	import IconType from "@shared-types/icon.type";
	import ColorType from "@shared-types/color.type";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import KeyboardShortcut from "@shared-components/accessibility/keyboard-shortcut.vue";
	import LoadingButtonIndicator from "@shared-components/loading/loading-button-indicator.vue";
	import { inject } from "vue";
	import { hapticInjectionKey } from "@shared-utils/accessibility/haptic";
	import { useColorStyle } from "@shared-utils/colors.utils";

	// Haptic au click (leaf) : le `:disabled` natif du bouton bloque déjà quand désactivé/loading.
	// Web/admin ne fournissent pas le hook → no-op. Dédup par geste côté provider (voir App.vue ionic).
	const triggerHaptic = inject(hapticInjectionKey, () =>
	{
	});

	defineProps<{
		title?: string,
		label?: string,
		labelClass?: string,
		icon?: IconType,
		iconSuffix?: IconType,
		disabled?: boolean,
		loading?: boolean,
		shortcut?: string,
		shortcutShift?: boolean,
		shortcutControl?: boolean,
		/** `compact` = 28px : sous la cible tactile de 44px → admin desktop uniquement, jamais web/ionic. */
		size?: "default" | "compact",
		color?: ColorType,
		active?: boolean,
		muted?: boolean,
	}>();
</script>

<style scoped>
	@reference "@main-css";

	.action-wrapper {
		--action-icon-size: 18px;

		@variant accessibility-font-size-150 {
			--action-icon-size: 19px;
		}

		@variant accessibility-font-size-200 {
			--action-icon-size: 20px;
		}

		@apply flex flex-row items-center gap-[8px];

		.action {
			@apply shrink-0 min-w-[44px] h-[44px] rounded-[8px] relative;
			@apply flex flex-row items-center justify-center p-[8px];
			@apply gap-[8px];

			@variant accessibility-font-size-150 {
				@apply min-w-[46px] h-[46px];

				@variant accessibility-font-size-200 {
					@apply min-w-[48px] h-[48px];
				}
			}

			&.has-label {
				@apply w-auto;

				.action-label {
					@apply text-[17px] opacity-80 truncate;
					@apply accessibility-font-size-150:text-[16px];
					@apply accessibility-font-size-200:text-[18px];
				}
			}

			&:deep([data-icon]) {
				@apply w-[18px] h-[18px] transition-transform;

				@variant accessibility-font-size-150 {
					@apply w-[19px] h-[19px];

					@variant accessibility-font-size-200 {
						@apply w-[20px] h-[20px];
					}
				}
			}

			@apply cursor-pointer;
			@apply hover:bg-gray-3 dark:hover:bg-dark-2 hover:opacity-90;
			@apply active:bg-gray-3 dark:active:bg-dark-2 active:opacity-90;

			/* Rotation = affordance hover (desktop). Gate `@media (hover: hover)` : sur tactile le `:hover`
			 * colle après le tap et laissait l'icône tournée (ne revenait pas à l'état d'origine). */
			@media (hover: hover) {
				&:hover:deep(.action-icon),
				&:active:deep(.action-icon) {
					@apply rotate-icon;
				}
			}

			@apply accessibility-hover-on-focus;
			@apply focus-visible:element-kbd-focus;

			/* Modes contraste élevé / noir-et-blanc : le bg-gray-3 du hover est trop pâle pour signaler
			 * l'interactivité. On ajoute un outline noir/blanc bien visible (sans saut de layout). */
			@variant accessibility-color-high-contrast {
				&:hover,
				&:active {
					@apply outline outline-2 outline-offset-0 outline-black dark:outline-white;
				}
			}

			@variant accessibility-color-black-and-white {
				&:hover,
				&:active {
					@apply outline outline-2 outline-offset-0 outline-black dark:outline-white;
				}
			}

			&[data-active="true"] {
				@apply bg-gray-3 dark:bg-dark-2 opacity-100;
			}

			&[data-muted="true"] {
				@apply opacity-40;

				&:hover {
					@apply opacity-100;
				}
			}

			&.disabled {
				@apply opacity-30 pointer-events-none;
			}
		}

		&[data-size="compact"] {
			.action {
				@apply min-w-[28px] h-[28px] rounded-[6px] py-[4px] px-[6px] gap-[4px];
				@apply text-sm whitespace-nowrap;

				@variant accessibility-font-size-150 {
					@apply min-w-[30px] h-[30px];

					@variant accessibility-font-size-200 {
						@apply min-w-[32px] h-[32px];
					}
				}

				&:deep([data-icon]) {
					@apply w-[16px] h-[16px];

					@variant accessibility-font-size-150 {
						@apply w-[17px] h-[17px];

						@variant accessibility-font-size-200 {
							@apply w-[18px] h-[18px];
						}
					}
				}
			}
		}

		&[data-color] {
			.action {
				@apply hover:bg-(--color)/10 dark:hover:bg-(--dark-color)/10;
				@apply hover:text-(--color) dark:hover:text-(--dark-color);
				@apply active:bg-(--color)/10 dark:active:bg-(--dark-color)/10;

				&[data-active="true"] {
					@apply bg-(--color)/10 dark:bg-(--dark-color)/10;
					@apply text-(--color) dark:text-(--dark-color);
				}
			}
		}
	}
</style>
